import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import '../styles/Hero.css';

const Hero = () => {
  const { t } = useTranslation();
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  useEffect(() => {
    // Use requestAnimationFrame for smoother animation
    let animationFrameId;
    
    const updateClock = () => {
      setTime(new Date());
      animationFrameId = requestAnimationFrame(updateClock);
    };
    
    // Start the animation
    animationFrameId = requestAnimationFrame(updateClock);
    
    return () => {
      // Clean up
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  // Calculate more precise hand rotations including milliseconds for smoother second hand
  const hourDegrees = (hours * 30) + (minutes * 0.5);
  const minuteDegrees = (minutes * 6) + (seconds * 0.1);
  const secondDegrees = (seconds * 6) + (milliseconds * 0.006);

  const titleVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.4,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.2,
        delay: 0.2,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
  };

  return (
    <section className="hero" ref={ref}>
      <div className="hero-content container">
        <div className="hero-text">
          <motion.h1
            initial="hidden"
            animate={controls}
            variants={titleVariants}
          >
            {t('hero.title')}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate={controls}
            variants={subtitleVariants}
          >
            {t('hero.subtitle')}
          </motion.p>
          <motion.button
            className="hero-button"
            initial="hidden"
            animate={controls}
            variants={buttonVariants}
            whileHover="hover"
            whileTap={{ scale: 0.95 }}
            onClick={() => window.open('https://www.instagram.com/kainwatches/', '_blank')}
          >
            {t('hero.cta')}
            <svg className="instagram-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </motion.button>
        </div>
        <motion.div
          className="hero-image"
          initial="hidden"
          animate={controls}
          variants={imageVariants}
        >
          <div className="watch-container">
            <div className="watch-placeholder">
              <div className="watch-face">
                <div className="watch-brand">KAIN</div>
                <div className="floating-star"></div>
                <div className="floating-star"></div>
                <div className="watch-shine"></div>
                <div className="watch-particles"></div>
                
                {/* Day indicator */}
                <div className="day-indicator">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                </div>
                
                {/* Date window */}
                <div className="date-window">
                  {new Date().getDate()}
                </div>
                
                {/* Small seconds subdial */}
                <div className="subdial">
                  <div 
                    className="subdial-hand"
                    style={{ transform: `rotate(${secondDegrees}deg)` }}
                  ></div>
                </div>
                
                {/* Concentric rings for luxury detail */}
                <div className="concentric-ring"></div>
                <div className="concentric-ring"></div>
                
                {/* Diamond hour markers */}
                {[...Array(8)].map((_, i) => (
                  <div 
                    key={`diamond-${i}`} 
                    className="diamond-marker"
                  ></div>
                ))}
                
                <div className="watch-hands">
                  <div 
                    className="hour-hand" 
                    style={{ transform: `rotate(${hourDegrees}deg)` }}
                  ></div>
                  <div 
                    className="minute-hand" 
                    style={{ transform: `rotate(${minuteDegrees}deg)` }}
                  ></div>
                  <div 
                    className="second-hand" 
                    style={{ transform: `rotate(${secondDegrees}deg)` }}
                  ></div>
                  <div className="watch-center"></div>
                </div>
                <div className="watch-markers">
                  {[...Array(12)].map((_, i) => (
                    <div 
                      key={i} 
                      className="marker" 
                      style={{ 
                        transform: `rotate(${i * 30}deg)`,
                        '--i': i
                      }}
                    >
                      <div className="marker-dot"></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="reflection"></div>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="scroll-indicator">
        <motion.div
          className="scroll-arrow"
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: 'loop',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12l7 7 7-7"/>
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero; 